import type { ErrorSource, ErrorSeverity } from './error-types';

// Error code information
export interface ErrorCodeInfo {
  code: string;
  source: ErrorSource;
  severity: ErrorSeverity;
  description: string;
  resolution?: string;
}

// Complete error code registry with descriptions
export const ERROR_CODE_REGISTRY: Record<string, ErrorCodeInfo> = {
  // ============================================================
  // LLM Gateway Error Codes
  // ============================================================
  'RATE_LIMIT': {
    code: 'RATE_LIMIT',
    source: 'llm',
    severity: 'error',
    description: 'Provider rate limit exceeded',
    resolution: 'Wait and retry, or switch provider',
  },
  'AUTH_ERROR': {
    code: 'AUTH_ERROR',
    source: 'llm',
    severity: 'error',
    description: 'API key or authentication failure',
    resolution: 'Check API key configuration',
  },
  'INVALID_REQUEST': {
    code: 'INVALID_REQUEST',
    source: 'llm',
    severity: 'error',
    description: 'Malformed request to provider',
    resolution: 'Check request payload format',
  },
  'PROVIDER_ERROR': {
    code: 'PROVIDER_ERROR',
    source: 'llm',
    severity: 'error',
    description: 'Provider returned error response',
    resolution: 'Check provider status page',
  },
  'TIMEOUT': {
    code: 'TIMEOUT',
    source: 'llm',
    severity: 'error',
    description: 'Request timed out',
    resolution: 'Retry or check provider status',
  },
  'UNKNOWN': {
    code: 'UNKNOWN',
    source: 'llm',
    severity: 'error',
    description: 'Unexpected error occurred',
    resolution: 'Check logs for details',
  },

  // ============================================================
  // Compiler Error Codes (7 errors)
  // ============================================================
  'UNEXPECTED_TOKEN': {
    code: 'UNEXPECTED_TOKEN',
    source: 'compiler',
    severity: 'error',
    description: 'Unexpected token in input',
    resolution: 'Check template syntax',
  },
  'SYNTAX_ERROR': {
    code: 'SYNTAX_ERROR',
    source: 'compiler',
    severity: 'error',
    description: 'Invalid syntax in template',
    resolution: 'Review and fix template syntax',
  },
  'UNDEFINED_REFERENCE': {
    code: 'UNDEFINED_REFERENCE',
    source: 'compiler',
    severity: 'error',
    description: 'Reference to undefined variable or section',
    resolution: 'Define the referenced variable',
  },
  'TYPE_MISMATCH': {
    code: 'TYPE_MISMATCH',
    source: 'compiler',
    severity: 'error',
    description: 'Type mismatch in operation',
    resolution: 'Ensure types are compatible',
  },
  'MISSING_REQUIRED': {
    code: 'MISSING_REQUIRED',
    source: 'compiler',
    severity: 'error',
    description: 'Required field is missing',
    resolution: 'Provide the required field',
  },
  'DUPLICATE_KEY': {
    code: 'DUPLICATE_KEY',
    source: 'compiler',
    severity: 'error',
    description: 'Duplicate key found',
    resolution: 'Remove duplicate key',
  },
  'INTERNAL_ERROR': {
    code: 'INTERNAL_ERROR',
    source: 'compiler',
    severity: 'error',
    description: 'Internal compiler error',
    resolution: 'Report to development team',
  },

  // ============================================================
  // Compiler Warning Codes (12 warnings)
  // ============================================================
  'DEPRECATED_SYNTAX': {
    code: 'DEPRECATED_SYNTAX',
    source: 'compiler',
    severity: 'warning',
    description: 'Deprecated syntax usage',
    resolution: 'Update to new syntax',
  },
  'UNUSED_VARIABLE': {
    code: 'UNUSED_VARIABLE',
    source: 'compiler',
    severity: 'warning',
    description: 'Variable declared but never used',
    resolution: 'Remove or use the variable',
  },
  'MISSING_OPTIONAL': {
    code: 'MISSING_OPTIONAL',
    source: 'compiler',
    severity: 'warning',
    description: 'Optional field is missing',
    resolution: 'Consider adding the field',
  },
  'STYLE_VIOLATION': {
    code: 'STYLE_VIOLATION',
    source: 'compiler',
    severity: 'warning',
    description: 'Code style violation',
    resolution: 'Follow style guidelines',
  },
  'INCOMPLETE_SECTION': {
    code: 'INCOMPLETE_SECTION',
    source: 'compiler',
    severity: 'warning',
    description: 'Section appears incomplete',
    resolution: 'Review and complete section',
  },
  'AMBIGUOUS_REFERENCE': {
    code: 'AMBIGUOUS_REFERENCE',
    source: 'compiler',
    severity: 'warning',
    description: 'Reference could match multiple targets',
    resolution: 'Make reference more specific',
  },
  'PERFORMANCE_HINT': {
    code: 'PERFORMANCE_HINT',
    source: 'compiler',
    severity: 'warning',
    description: 'Potential performance issue',
    resolution: 'Consider optimization',
  },
  'COMPATIBILITY_WARNING': {
    code: 'COMPATIBILITY_WARNING',
    source: 'compiler',
    severity: 'warning',
    description: 'Compatibility issue detected',
    resolution: 'Check compatibility requirements',
  },
  'IMPLICIT_CONVERSION': {
    code: 'IMPLICIT_CONVERSION',
    source: 'compiler',
    severity: 'warning',
    description: 'Implicit type conversion',
    resolution: 'Use explicit conversion',
  },
  'POSSIBLE_TYPO': {
    code: 'POSSIBLE_TYPO',
    source: 'compiler',
    severity: 'warning',
    description: 'Possible typo detected',
    resolution: 'Verify spelling',
  },
  'REDUNDANT_CODE': {
    code: 'REDUNDANT_CODE',
    source: 'compiler',
    severity: 'warning',
    description: 'Redundant code detected',
    resolution: 'Remove redundant code',
  },
  'BEST_PRACTICE': {
    code: 'BEST_PRACTICE',
    source: 'compiler',
    severity: 'warning',
    description: 'Best practice suggestion',
    resolution: 'Follow best practices',
  },

  // ============================================================
  // Auth Error Codes
  // ============================================================
  'SESSION_EXPIRED': {
    code: 'SESSION_EXPIRED',
    source: 'auth',
    severity: 'error',
    description: 'User session has expired',
    resolution: 'User needs to log in again',
  },
  'INVALID_TOKEN': {
    code: 'INVALID_TOKEN',
    source: 'auth',
    severity: 'error',
    description: 'Invalid or malformed token',
    resolution: 'Clear tokens and re-authenticate',
  },
  'BOOTSTRAP_ERROR': {
    code: 'BOOTSTRAP_ERROR',
    source: 'auth',
    severity: 'error',
    description: 'Session bootstrap failed',
    resolution: 'Check session-bootstrap function',
  },
  'UNAUTHORIZED': {
    code: 'UNAUTHORIZED',
    source: 'auth',
    severity: 'error',
    description: 'User is not authorized',
    resolution: 'Check user permissions',
  },

  // ============================================================
  // Edge Function Error Codes
  // ============================================================
  'FUNCTION_TIMEOUT': {
    code: 'FUNCTION_TIMEOUT',
    source: 'function',
    severity: 'error',
    description: 'Edge function timed out',
    resolution: 'Optimize function or increase timeout',
  },
  'FUNCTION_ERROR': {
    code: 'FUNCTION_ERROR',
    source: 'function',
    severity: 'error',
    description: 'Edge function execution error',
    resolution: 'Check function logs',
  },
  'FUNCTION_NOT_FOUND': {
    code: 'FUNCTION_NOT_FOUND',
    source: 'function',
    severity: 'error',
    description: 'Edge function not found',
    resolution: 'Deploy the function',
  },

  // ============================================================
  // Extraction Error Codes
  // ============================================================
  'EXTRACTION_FAILED': {
    code: 'EXTRACTION_FAILED',
    source: 'extraction',
    severity: 'error',
    description: 'Entity extraction failed',
    resolution: 'Check extraction rules',
  },
  'PATTERN_MISMATCH': {
    code: 'PATTERN_MISMATCH',
    source: 'extraction',
    severity: 'warning',
    description: 'Pattern did not match expected structure',
    resolution: 'Review pattern configuration',
  },
  'LOW_CONFIDENCE': {
    code: 'LOW_CONFIDENCE',
    source: 'extraction',
    severity: 'warning',
    description: 'Extraction confidence below threshold',
    resolution: 'Review extraction result',
  },
};

// Get error code info with fallback for unknown codes
export function getErrorCodeInfo(code: string, defaultSource: ErrorSource = 'compiler'): ErrorCodeInfo {
  const info = ERROR_CODE_REGISTRY[code];
  if (info) {
    return info;
  }

  // Return a generic info object for unknown codes
  return {
    code,
    source: defaultSource,
    severity: 'error',
    description: `Unknown error code: ${code}`,
    resolution: 'Check logs for details',
  };
}

// Get severity badge variant
export function getSeverityVariant(severity: ErrorSeverity): 'destructive' | 'warning' | 'secondary' {
  switch (severity) {
    case 'error':
      return 'destructive';
    case 'warning':
      return 'warning';
    case 'info':
    default:
      return 'secondary';
  }
}

// Edge function names for reference
export const EDGE_FUNCTIONS = {
  // radiologylive-ai (10 functions)
  ai: [
    'session-bootstrap',
    'admin-approve-user',
    'onboarding-complete',
    'onboarding-identity',
    'onboarding-preferences',
    'onboarding-role',
    'create-checkout-session',
    'manage-subscription',
    'stripe-webhook',
    'purge-audit-logs',
  ],
  // radiologylive-speak (8 functions)
  speak: [
    'compile-report',
    'fetch-template',
    'live-insight',
    'process-dictation',
    'report-create',
    'report-delete',
    'report-get',
    'report-update',
  ],
} as const;
